import _ from "lodash";
/** -- external -- */
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";

/** -- internal -- */
import { getUserCoursesV2Query } from "Courses/modules/CoursePaginationQuery";
import {
  getUserCourseTagsFilters,
  getPaginationCourseFilters,
  mapleBearLikeCentralOrg,
} from "Courses/modules/utils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { prepareLoadMoreFeed } from "Utils";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { getCurrentCurriculumContextForJumpTo } from "modules/CommonSelectors";
import {
  getIsLearningCoursesEnabled,
  COURSE_TYPE,
} from "LearningCourses/modules/Utils";
import {
  getCoursesFeedQuery,
  getClassesFeedQuery,
} from "LearningCoursesList/modules/LearningCoursesListQueries";
import {
  getClassesFeedQueryVariables,
  getCoursesFeedQueryVariables,
} from "LearningCoursesList/modules/Utils";
import { isBlueprintTeacherCourseAssociationEnabled } from "LearningCourse/modules/LearningCourseUtils";

const PAGE_SIZE = 20;

const ARCHIVAL_STATE = "ACTIVE";

const CourseFeedWrapperForPaidProgram = ({
  children,
  data,
  networkStatus,
  hasNextPage,
  loadMoreFeed,
  courseGradeFilters,
  isData,
  isLoading,
}) => {
  return children({
    data,
    networkStatus,
    hasNextPage,
    loadMoreFeed,
    courseGradeFilters,
    isData,
    isLoading,
  });
};

const mapStateToProps = (state, ownProps) => {
  const { searchText = "" } = ownProps;

  const userInfo = getUserInfo({ portalType: "PLANNER" });
  const userId = userInfo?.id;

  const organizationId = userInfo.org_id;

  const organizationDetails = getOrganizationDetailsFromCache(organizationId);

  const organizationTags = _.get(organizationDetails, "tags", []);

  const {
    id: curriculumProgramId,
    type: currentCurriculumProgramType,
    nodeInterfaceType: curriculumNodeInterfaceType,
  } = getCurrentCurriculumContextForJumpTo(state);

  const selectedAcademicYear = _.get(
    state,
    "platform.academicYearSelected",
    {}
  );

  const {
    isCurrentAcademicYear = true,
    id: academicYear = "",
  } = selectedAcademicYear;

  const userTags = state.login.userInfo?.tags || [];
  const mapleBearUserBasedFilters = getUserCourseTagsFilters({ userTags });

  const courseFilters = getPaginationCourseFilters({
    curriculumProgramId,
    selectedArchivalState: ARCHIVAL_STATE,
    classSearchText: searchText,
    isMapleBearLikeOrg: mapleBearLikeCentralOrg({
      organizationTags,
    }),
    selectedGrades: [],
    selectedRegions: [],
    selectedSubjects: [],
    selectedCourseLevels: [],
    selectedSubjectGroups: [],
    mapleBearUserBasedFilters,
    options: {
      grades: [],
      regions: [],
      subjects: [],
      courseLevels: [],
      subjectGroups: [],
    },
    currentCurriculumProgramType,
    ...(!isCurrentAcademicYear && { academicYear }),
  });

  const courseGradeFilters = {
    academicYearId: academicYear,
  };

  const archivedCourseFilters = {
    archivalState: "ARCHIVED",
    isOrderByStared: true,
    curriculumProgramIds: [curriculumProgramId],
  };

  archivedCourseFilters.curriculumProgramIds = [curriculumProgramId];

  if (!isCurrentAcademicYear) {
    archivedCourseFilters.academicYear = academicYear;
  }

  if (mapleBearUserBasedFilters?.tags?.length > 0) {
    archivedCourseFilters.tags = mapleBearUserBasedFilters?.tags;
  }

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled({
    curriculumProgramId,
  });

  const classesFeedQueryVariables = getClassesFeedQueryVariables({
    organizationId,
    curriculumProgramIds: [curriculumProgramId],
    academicYearId: academicYear,
    filters: {
      searchText,
    },
  });

  const isBlueprintCourseEnabled = isBlueprintTeacherCourseAssociationEnabled();

  const coursesFeedQueryVariables = getCoursesFeedQueryVariables({
    organizationId,
    curriculumProgramIds: [curriculumProgramId],
    academicYearId: academicYear,
    filters: {
      searchText,
      types: isBlueprintCourseEnabled ? [] : [COURSE_TYPE.TEACHER],
    },
  });

  return {
    isData: true,
    isLoading: false,
    userId,
    courseFilters,
    courseGradeFilters,
    archivedCourseFilters,
    curriculumProgramId,
    isLearningCoursesEnabled,
    classesFeedQueryVariables,
    coursesFeedQueryVariables,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getCoursesFeedQuery, {
    name: "getCoursesFeed",
    skip: ({ isLearningCoursesFeed }) => !isLearningCoursesFeed,
    options: ({ coursesFeedQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: coursesFeedQueryVariables,
    }),
    props({ getCoursesFeed, ownProps: { isData, isLoading } }) {
      const { node, variables, networkStatus } = getCoursesFeed;

      const data = _.get(node, "learningCourses", {});
      const learningCoursesList = _.get(data, "edges", []);
      const pageInfo = _.get(data, "pageInfo", {});
      const hasNextPage = _.get(pageInfo, "hasNextPage", false);
      const endCursor = _.get(pageInfo, "endCursor", null);

      return {
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        data: learningCoursesList,
        hasNextPage,
        isFetchingMoreData: networkStatus === 3,
        loadMoreFeed: () => {
          getCoursesFeed.fetchMore({
            variables: {
              ...variables,
              after: endCursor,
            },
          });
        },
      };
    },
  }),
  graphql(getClassesFeedQuery, {
    name: "getClassesFeed",
    skip: ({
      curriculumProgramId,
      isLearningCoursesEnabled,
      isLearningCoursesFeed,
    }) =>
      _.isEmpty(curriculumProgramId) ||
      !isLearningCoursesEnabled ||
      isLearningCoursesFeed,
    options: ({ classesFeedQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: classesFeedQueryVariables,
    }),
    props({ getClassesFeed, ownProps: { isData, isLoading } }) {
      const { node, variables, networkStatus } = getClassesFeed;

      const data = _.get(node, "courses", {});
      const classesList = _.get(data, "edges", []);
      const pageInfo = _.get(data, "pageInfo", {});
      const hasNextPage = _.get(pageInfo, "hasNextPage", false);
      const endCursor = _.get(pageInfo, "endCursor", null);

      return {
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        data: classesList,
        hasNextPage,
        isFetchingMoreData: networkStatus === 3,
        loadMoreFeed: () => {
          getClassesFeed.fetchMore({
            variables: {
              ...variables,
              after: endCursor,
            },
          });
        },
      };
    },
  }),
  graphql(getUserCoursesV2Query, {
    name: "getUserCoursesV2Query",
    skip: ({ curriculumProgramId, isLearningCoursesEnabled }) =>
      _.isEmpty(curriculumProgramId) || isLearningCoursesEnabled,
    options: ({
      userId,
      courseFilters,
      courseGradeFilters,
      archivedCourseFilters,
    }) => ({
      variables: {
        userId,
        first: PAGE_SIZE,
        courseFilters,
        courseGradeFilters,
        archivedCourseFilters,
        portalType: "PLANNER",
      },
      fetchPolicy: "cache-first",
    }),
    props: ({ getUserCoursesV2Query, ownProps: { isData, isLoading } }) => {
      const { hasNextPage, endCursor } = _.get(
        getUserCoursesV2Query,
        "node.coursesV2.pageInfo",
        {}
      );

      return {
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getUserCoursesV2Query.networkStatus
          ) || isLoading,
        isData: !!_.get(getUserCoursesV2Query, "node") && isData,
        data: _.get(getUserCoursesV2Query, "node.coursesV2.edges", []),
        loadMoreFeed: prepareLoadMoreFeed({
          query: getUserCoursesV2Query,
          variables: {
            ...getUserCoursesV2Query.variables,
            after: endCursor,
          },
          queryData: getUserCoursesV2Query,
          customPathString: "node.coursesV2",
        }),
        hasNextPage,
        networkStatus: getUserCoursesV2Query.networkStatus,
      };
    },
  })
)(CourseFeedWrapperForPaidProgram);
